import {
  collection,
  doc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  Unsubscribe,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { apiClient } from './apiClient';
import {
  Contact,
  Conversation,
  ConversationFilters,
  ConversationNote,
  ConversationStatus,
  canSendFreeform
} from '../../types/models/whatsapp';

const CONVERSATIONS = 'whatsapp_conversations';
const CONTACTS = 'whatsapp_contacts';
const DEFAULT_PAGE_SIZE = 50;

/**
 * Conversations are read live from Firestore (the inbox must update without
 * polling) but written only through the API, so authorization and the 24h
 * window stay server-enforced. Firestore rules deny client writes.
 */
class ConversationService {
  private mapConversation(id: string, data: any): Conversation {
    return {
      id,
      contactId: data.contactId,
      phoneNumberId: data.phoneNumberId,
      status: data.status || 'open',
      assigneeId: data.assigneeId ?? null,
      assigneeName: data.assigneeName ?? null,
      tags: Array.isArray(data.tags) ? data.tags : [],
      lastInboundAt: data.lastInboundAt ?? null,
      windowExpiresAt: data.windowExpiresAt ?? null,
      // Conversations created before bot support default to bot-on, matching
      // the server default for new conversations.
      botEnabled: data.botEnabled !== false,
      unreadCount: data.unreadCount || 0,
      lastMessagePreview: data.lastMessagePreview || '',
      lastMessageAt: data.lastMessageAt || 0,
      contactName: data.contactName || '',
      contactPhone: data.contactPhone || '',
      createdAt: data.createdAt || 0,
      updatedAt: data.updatedAt || 0
    };
  }

  /**
   * Live conversation list, filtered by the two fields Firestore can index.
   *
   * Deliberately narrow: search, tags, and window state are NOT accepted here.
   * They change on every keystroke, and folding them into the subscription key
   * would recreate the listener constantly — wasting reads and tripping
   * firebase-js-sdk#9267. Pass the full filters to `applyClientFilters` instead.
   */
  subscribeToConversations(
    filters: Pick<ConversationFilters, 'status' | 'assigneeId'>,
    currentAgentId: string | null,
    onChange: (conversations: Conversation[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    const constraints: QueryConstraint[] = [];

    if (filters.status && filters.status !== 'all') {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters.assigneeId && filters.assigneeId !== 'all') {
      if (filters.assigneeId === 'unassigned') {
        constraints.push(where('assigneeId', '==', null));
      } else if (filters.assigneeId === 'me') {
        if (!currentAgentId) {
          onChange([]);
          return () => {};
        }
        constraints.push(where('assigneeId', '==', currentAgentId));
      } else {
        constraints.push(where('assigneeId', '==', filters.assigneeId));
      }
    }

    constraints.push(orderBy('lastMessageAt', 'desc'));
    constraints.push(fsLimit(DEFAULT_PAGE_SIZE));

    const q = query(collection(db, CONVERSATIONS), ...constraints);

    return onSnapshot(
      q,
      (snapshot) => {
        // Raw query results only. Callers apply `applyClientFilters` themselves,
        // so refining a search never has to recreate this listener.
        onChange(snapshot.docs.map((d) => this.mapConversation(d.id, d.data())));
      },
      (error) => {
        console.error('❌ Conversation subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  /**
   * Search, tag, and window-state filtering. Kept out of the Firestore query so
   * refining a result set never recreates the listener.
   */
  applyClientFilters(
    conversations: Conversation[],
    filters: ConversationFilters
  ): Conversation[] {
    let results = conversations;

    if (filters.search?.trim()) {
      const term = filters.search.trim().toLowerCase();
      // Only match on phone when the term has digits: ''.includes('') is true,
      // so a text-only search would otherwise match every contact.
      const digits = term.replace(/\D/g, '');
      results = results.filter(
        (c) =>
          c.contactName.toLowerCase().includes(term) ||
          (digits.length > 0 && c.contactPhone.includes(digits))
      );
    }

    if (filters.tags?.length) {
      results = results.filter((c) =>
        filters.tags!.every((tag) => c.tags.includes(tag))
      );
    }

    if (filters.windowState && filters.windowState !== 'all') {
      const wantOpen = filters.windowState === 'open';
      results = results.filter((c) => canSendFreeform(c) === wantOpen);
    }

    if (filters.unreadOnly) {
      results = results.filter((c) => c.unreadCount > 0);
    }

    if (filters.botState && filters.botState !== 'all') {
      const wantBotOn = filters.botState === 'on';
      results = results.filter((c) => c.botEnabled === wantBotOn);
    }

    return results;
  }

  /** Live single conversation — drives the header and composer window state. */
  subscribeToConversation(
    conversationId: string,
    onChange: (conversation: Conversation | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    if (!db) {
      onError?.(new Error('Firestore unavailable'));
      return () => {};
    }

    return onSnapshot(
      doc(db, CONVERSATIONS, conversationId),
      (snapshot) => {
        onChange(
          snapshot.exists()
            ? this.mapConversation(snapshot.id, snapshot.data())
            : null
        );
      },
      (error) => {
        console.error('❌ Conversation subscription error:', error);
        onError?.(error as Error);
      }
    );
  }

  /**
   * Live contact record for the open thread — drives the block and opt-out
   * controls, which must reflect changes made by the webhook (an inbound STOP)
   * without a refresh.
   */
  subscribeToContact(
    contactId: string,
    onChange: (contact: Contact | null) => void
  ): Unsubscribe {
    if (!db) return () => {};

    return onSnapshot(
      doc(db, CONTACTS, contactId),
      (snapshot) => {
        if (!snapshot.exists()) {
          onChange(null);
          return;
        }
        const data = snapshot.data();
        onChange({
          id: snapshot.id,
          phone: data.phone || '',
          name: data.name || '',
          attributes: data.attributes || {},
          tags: Array.isArray(data.tags) ? data.tags : [],
          optIn: data.optIn || {
            status: 'unknown',
            source: '',
            updatedAt: 0
          },
          blocked: data.blocked === true,
          blockedAt: data.blockedAt ?? null,
          blockedBy: data.blockedBy ?? null,
          linkedUserId: data.linkedUserId,
          lastSeenAt: data.lastSeenAt ?? null,
          createdAt: data.createdAt || 0,
          updatedAt: data.updatedAt || 0
        });
      },
      (error) => console.error('❌ Contact subscription error:', error)
    );
  }

  subscribeToNotes(
    conversationId: string,
    onChange: (notes: ConversationNote[]) => void
  ): Unsubscribe {
    if (!db) return () => {};

    const q = query(
      collection(db, CONVERSATIONS, conversationId, 'notes'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      onChange(
        snapshot.docs.map((d) => ({
          id: d.id,
          conversationId,
          body: d.data().body || '',
          authorId: d.data().authorId || '',
          authorName: d.data().authorName || '',
          createdAt: d.data().createdAt || 0
        }))
      );
    });
  }

  async assignConversation(
    conversationId: string,
    agentId: string | null
  ): Promise<void> {
    await apiClient.patch(`/conversations/${conversationId}`, { assigneeId: agentId });
  }

  async setStatus(
    conversationId: string,
    status: ConversationStatus
  ): Promise<void> {
    await apiClient.patch(`/conversations/${conversationId}`, { status });
  }

  async setTags(conversationId: string, tags: string[]): Promise<void> {
    await apiClient.patch(`/conversations/${conversationId}`, { tags });
  }

  async markRead(conversationId: string): Promise<void> {
    await apiClient.post(`/conversations/${conversationId}/read`);
  }

  async addNote(conversationId: string, body: string): Promise<void> {
    await apiClient.post(`/conversations/${conversationId}/notes`, { body });
  }

  /** Per-conversation automation toggle. */
  async setBotEnabled(
    conversationId: string,
    botEnabled: boolean
  ): Promise<void> {
    await apiClient.patch(`/conversations/${conversationId}`, { botEnabled });
  }

  /**
   * Clears the automation's stored state for this conversation, so the next
   * inbound message starts a flow from the beginning. The escape hatch for a
   * contact stuck mid-flow.
   */
  async clearBotSession(conversationId: string): Promise<void> {
    await apiClient.post(`/conversations/${conversationId}/clear-bot-session`);
  }

  /** Agent-side block, distinct from the contact's own opt-out. */
  async setContactBlocked(contactId: string, blocked: boolean): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, { blocked });
  }

  /** Manual opt-in/opt-out override, e.g. after a phone conversation. */
  async setContactOptIn(
    contactId: string,
    optInStatus: 'opted_in' | 'opted_out' | 'unknown'
  ): Promise<void> {
    await apiClient.patch(`/contacts/${contactId}`, { optInStatus });
  }
}

export const conversationService = new ConversationService();
export default conversationService;
